import type { ApiUser } from '../api/types';

import {
  ACCOUNT_QUERY,
  DATA_BROADCAST_CHANNEL_NAME as LEGACY_DATA_BROADCAST_CHANNEL_NAME,
  ESTABLISH_BROADCAST_CHANNEL_NAME as LEGACY_ESTABLISH_BROADCAST_CHANNEL_NAME,
  GLOBAL_STATE_CACHE_KEY as LEGACY_GLOBAL_STATE_CACHE_KEY,
  MULTITAB_LOCALSTORAGE_KEY as LEGACY_MULTITAB_LOCALSTORAGE_KEY,
  SESSION_ACCOUNT_PREFIX,
} from '../config';
import { getAccountSlot } from './accountSlot';

export type SharedSessionData = {
  dcId: number;
  userId?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  isPremium?: boolean;
  color?: number;
  isTest?: boolean;
  avatarHash?: string;
  avatarUri?: string;
  hasSessionData?: boolean;
  sessionEncrypted?: string;
  sessionRevision?: string;
  dc1_auth_key?: string | number[];
  dc2_auth_key?: string | number[];
  dc3_auth_key?: string | number[];
  dc4_auth_key?: string | number[];
  dc5_auth_key?: string | number[];
  dc1_hash?: string | number[];
  dc2_hash?: string | number[];
  dc3_hash?: string | number[];
  dc4_hash?: string | number[];
  dc5_hash?: string | number[];
};

export type AccountInfo = Pick<SharedSessionData, (
  'userId' | 'firstName' | 'lastName' | 'phone' | 'isPremium' | 'color' | 'isTest' | 'avatarHash' | 'avatarUri'
)> & { userId: string };

const WORKER_NAME = typeof WorkerGlobalScope !== 'undefined'
  && globalThis instanceof WorkerGlobalScope
  ? (globalThis as typeof globalThis & { name?: string }).name
  : undefined;
const WORKER_ACCOUNT_SLOT = WORKER_NAME
  ? Number(new URLSearchParams(WORKER_NAME).get(ACCOUNT_QUERY))
  : undefined;
const PAGE_ACCOUNT_SLOT = typeof globalThis.location !== 'undefined'
  ? getAccountSlot(globalThis.location.href)
  : undefined;

export const ACCOUNT_SLOT = WORKER_ACCOUNT_SLOT && WORKER_ACCOUNT_SLOT > 1
  ? WORKER_ACCOUNT_SLOT
  : PAGE_ACCOUNT_SLOT;
const SLOT_SUFFIX = ACCOUNT_SLOT || 1;
const DC_IDS = [1, 2, 3, 4, 5];
export const ACCOUNT_INFO_CHANGED_EVENT = 'account-info-changed';

export const DATA_BROADCAST_CHANNEL_NAME = `${LEGACY_DATA_BROADCAST_CHANNEL_NAME}_${SLOT_SUFFIX}`;
export const ESTABLISH_BROADCAST_CHANNEL_NAME = `${LEGACY_ESTABLISH_BROADCAST_CHANNEL_NAME}_${SLOT_SUFFIX}`;
export const MULTITAB_STORAGE_KEY = `${LEGACY_MULTITAB_LOCALSTORAGE_KEY}_${SLOT_SUFFIX}`;

export function getGlobalStateCacheKey(slot = ACCOUNT_SLOT) {
  return slot && slot !== 1 ? `${LEGACY_GLOBAL_STATE_CACHE_KEY}_${slot}` : LEGACY_GLOBAL_STATE_CACHE_KEY;
}

export const GLOBAL_STATE_CACHE_KEY = getGlobalStateCacheKey();

function canReadSessionStorage() {
  return typeof localStorage !== 'undefined';
}

export function getAccountSlots() {
  if (!canReadSessionStorage()) return [];

  return Object.keys(localStorage)
    .filter((key) => key.startsWith(SESSION_ACCOUNT_PREFIX))
    .map((key) => Number(key.slice(SESSION_ACCOUNT_PREFIX.length)))
    .filter((slot) => slot > 0 && Number.isInteger(slot))
    .sort((first, second) => first - second);
}

export function getAccountsInfo() {
  const accounts: Record<number, AccountInfo> = {};
  getAccountSlots().forEach((slot) => {
    const session = loadSlotSession(slot);
    if (session?.userId) {
      accounts[slot] = {
        userId: session.userId,
        firstName: session.firstName,
        lastName: session.lastName,
        phone: session.phone,
        isPremium: session.isPremium,
        color: session.color,
        isTest: session.isTest,
        avatarHash: session.avatarHash,
        avatarUri: session.avatarUri,
      };
    }
  });
  return accounts;
}

export function getFirstLoggedInAccountSlot(accounts = getAccountsInfo()) {
  return Object.keys(accounts).map(Number).sort((a, b) => a - b)[0];
}

export function loadSlotSession(slot: number | undefined): SharedSessionData | undefined {
  if (!canReadSessionStorage()) return undefined;

  try {
    const data = JSON.parse(localStorage.getItem(`${SESSION_ACCOUNT_PREFIX}${slot || 1}`) || '{}');
    return data?.dcId ? data as SharedSessionData : undefined;
  } catch {
    return undefined;
  }
}

export function storeAccountData(slot: number | undefined, data: Partial<SharedSessionData>) {
  if (!canReadSessionStorage()) return;

  const current = loadSlotSession(slot);
  if (!current) return;

  // Migration owns removal of legacy keys, after the encrypted replacement is committed.
  if (!current.sessionEncrypted && DC_IDS.some((dcId) => current[`dc${dcId}_auth_key` as keyof SharedSessionData])) {
    return;
  }

  const updated = omitPlainSessionSecrets({ ...current, ...data });
  if (!updated.userId) return;

  const json = JSON.stringify(updated);
  if (json !== JSON.stringify(current)) {
    localStorage.setItem(`${SESSION_ACCOUNT_PREFIX}${slot || 1}`, json);
  }
}

function omitPlainSessionSecrets(session: SharedSessionData) {
  const sanitized = { ...session };
  DC_IDS.forEach((dcId) => {
    delete sanitized[`dc${dcId}_auth_key` as keyof SharedSessionData];
    delete sanitized[`dc${dcId}_hash` as keyof SharedSessionData];
  });
  return sanitized;
}

export function updateAccountFromUser(slot: number | undefined, user: ApiUser) {
  const account = loadSlotSession(slot);
  if (account?.userId && account.userId !== user.id) return;

  storeAccountData(slot, {
    userId: user.id,
    firstName: user.firstName,
    lastName: user.lastName,
    phone: user.phoneNumber,
    isPremium: user.isPremium,
    color: user.color?.color,
    avatarHash: user.avatarHash,
  });
}

export function getAccountSlotUrl(slot: number, forLogin = false, isTest?: boolean) {
  const url = new URL(globalThis.location.href);
  if (slot !== 1) {
    url.searchParams.set(ACCOUNT_QUERY, String(slot));
  } else {
    url.searchParams.delete(ACCOUNT_QUERY);
  }

  if (isTest === true) {
    url.searchParams.set('test', '1');
  } else if (isTest === false) {
    url.searchParams.delete('test');
  }

  url.hash = forLogin ? 'login' : '';
  return url.toString();
}
